import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  ShieldCheck,
  Phone,
  Calendar as CalIcon,
  Sun,
  Sunrise,
  Moon,
  Check,
  Users,
  MessageSquare,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Video as VideoIcon,
  DollarSign,
  Send,
  FileText,
  Truck,
  MapPin,
  Volume2,
  VolumeX,
  Award,
  Star,
  Zap,
  Home as HomeIcon,
  Trophy,
  Rocket,
  ClipboardList,
  Plus,
  Camera,
  X as XIcon,
  Clock,
  Play,
  Wrench,
  Wine,
  DoorOpen,
  Hammer,
  Radio,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { VIDEOS, LINKS, FOUNDER_TIMELINE, COPY, DEMO_VIDEOS } from "@/config/AssetSpine";

// ============================================================
// <VerticalCockpit config={...} palette={...} />
// ------------------------------------------------------------
// The Master Chassis. Renders the 6-module Grid cockpit for
// every vertical route (/green-shield · /uprising · /moto ·
// /sims · /mobile-cycle). Route files stay as thin wrappers.
//
// PROPS
//   config   — Industry vocab dict from /config/verticalConfig.js
//   palette  — Skin object from PALETTES in /config/AssetSpine.js
// ============================================================

const HERO_VIDEO_URL = VIDEOS.AMANDA_FOUNDER;
const FOUNDER_CTA_URL = LINKS.JOIN_CTA;

// Slot windows shared across every intake calendar.
const SLOTS = [
  { key: "am", label: "Morning",   sub: "8a — 11a", icon: Sunrise },
  { key: "pm", label: "Afternoon", sub: "12p — 4p", icon: Sun },
];
const CALLBACK_WINDOWS = [
  { key: "morning",   label: "Morning",   sub: "8a — 11a", icon: Sunrise },
  { key: "afternoon", label: "Afternoon", sub: "12p — 4p", icon: Sun },
  { key: "evening",   label: "Evening",   sub: "5p — 7p",  icon: Moon },
];
const DAY_CHOICE = [
  { key: "today",    label: "Today",    sub: "Feb 03" },
  { key: "tomorrow", label: "Tomorrow", sub: "Feb 04" },
];
const CMD_DAYS = [
  { key: "mon", label: "Mon", date: "Feb 03" },
  { key: "tue", label: "Tue", date: "Feb 04" },
  { key: "wed", label: "Wed", date: "Feb 05" },
  { key: "thu", label: "Thu", date: "Feb 06" },
  { key: "fri", label: "Fri", date: "Feb 07" },
  { key: "sat", label: "Sat", date: "Feb 08" },
  { key: "sun", label: "Sun", date: "Feb 09" },
];
const TIME_BLOCKS = ["8am", "10am", "12pm", "2pm", "4pm"];

const INITIAL_BOOKING_GRID = {
  "mon-am": "crewA", "mon-pm": "open",
  "tue-am": "open",  "tue-pm": "crewB",
  "wed-am": "crewA", "wed-pm": "crewA",
  "thu-am": "open",  "thu-pm": "open",
  "fri-am": "crewB", "fri-pm": "open",
  "sat-am": "crewA", "sat-pm": "crewB",
  "sun-am": "open",  "sun-pm": "open",
};

// Icon per vertical for hero / brand stripe.
function tradeIcon(key) {
  switch (key) {
    case "MUSIC":        return VideoIcon;
    case "MOTO":         return Wrench;
    case "LOGISTICS":    return Truck;
    case "MOBILE_CYCLE": return Wrench;
    case "MIXOLOGY":     return Wine;
    case "MAGIC_GARAGE": return DoorOpen;
    case "MSQUARED":     return Hammer;
    case "AFFORDABLE":   return HomeIcon;
    case "LAWN":
    default:             return ShieldCheck;
  }
}

// ============================================================
// MODULES · 6-module anchor map for the locked vertical nav.
// The tabs in the minimal header scroll to these IDs. The global
// AmandaAvatar pin (mounted in App.js) tracks the currently-visible
// one via IntersectionObserver and reports it into the walk-through
// modal.
// ============================================================
const MODULE_NAV = [
  { id: "greeting", label: "Greeting", icon: Radio },
  { id: "shield",   label: "Shield",   icon: ShieldCheck },
  { id: "calendar", label: "Calendar", icon: CalIcon },
  { id: "spine",    label: "Spine",    icon: FileText },
  { id: "billing",  label: "Billing",  icon: DollarSign },
  { id: "growth",   label: "Growth",   icon: VideoIcon },
];

// ============================================================
// Root layout
// ============================================================
export default function VerticalCockpit({ config, palette, testPrefix = "vc" }) {
  const P = palette;
  const [activeModule, setActiveModule] = useState("greeting");

  // Watch the 6 section anchors so the header nav tabs highlight
  // the module currently in view. The global <AmandaAvatar/>
  // (App.js) runs its own observer against the same anchor IDs
  // for the walkthrough-modal context label.
  useEffect(() => {
    const nodes = MODULE_NAV
      .map((m) => document.getElementById(m.id))
      .filter(Boolean);
    if (nodes.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.getAttribute("id");
          if (id) setActiveModule(id);
        }
      },
      { rootMargin: "-30% 0px -40% 0px", threshold: [0.1, 0.45, 0.8] }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen text-white relative" style={{ background: P.BG }}>
      <Backdrop P={P} />

      {/* Locked vertical header — no foyer nav, no logo, no home link.
          Vertical brand + 6-module anchor tabs only. */}
      <header
        className="sticky top-0 z-30 border-b backdrop-blur-md"
        style={{
          borderColor: `${P.PRIMARY}33`,
          background:
            `linear-gradient(180deg, ${P.BG}F2 0%, ${P.BG}CC 100%)`,
        }}
        data-testid={`${testPrefix}-locked-header`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-4">
          <div
            className="flex items-center gap-3 min-w-0"
            data-testid={`${testPrefix}-brand-badge`}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{
                background: P.PRIMARY,
                boxShadow: `0 0 12px ${P.PRIMARY}, 0 0 0 4px ${P.PRIMARY}22`,
              }}
              aria-hidden
            />
            <div className="min-w-0">
              <div
                className="text-[9px] uppercase tracking-[0.32em] font-mono truncate"
                style={{ color: P.MUTED }}
              >
                {config.trade}
              </div>
              <div
                className="text-sm sm:text-base font-display uppercase tracking-[0.18em] truncate leading-tight"
                style={{ color: P.INK }}
              >
                {config.brand}
              </div>
            </div>
          </div>

          {/* Module anchor tabs · hidden on very small screens */}
          <nav
            className="hidden md:flex items-center gap-1"
            data-testid={`${testPrefix}-module-nav`}
            aria-label="Module navigation"
          >
            {MODULE_NAV.map((m) => {
              const isActive = activeModule === m.id;
              return (
                <a
                  key={m.id}
                  href={`#${m.id}`}
                  data-testid={`${testPrefix}-nav-${m.id}`}
                  className="text-[10px] uppercase tracking-[0.24em] font-mono px-2.5 py-1.5 rounded-sm transition-colors"
                  style={{
                    color: isActive ? P.INK : P.MUTED,
                    background: isActive ? `${P.PRIMARY}22` : "transparent",
                    borderBottom: isActive
                      ? `1px solid ${P.PRIMARY}`
                      : "1px solid transparent",
                  }}
                >
                  {m.label}
                </a>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 space-y-10">
        <section id="greeting" data-module="greeting" className="scroll-mt-24">
          <BrandStripe P={P} config={config} testPrefix={testPrefix} />
        </section>

        <section id="shield"   data-module="shield"   className="scroll-mt-24">
          <IntakeShield    P={P} config={config} testPrefix={testPrefix} />
        </section>
        <section id="calendar" data-module="calendar" className="scroll-mt-24">
          <CommandCalendar P={P} config={config} testPrefix={testPrefix} />
        </section>
        <section id="spine"    data-module="spine"    className="scroll-mt-24">
          <UtilitySpine    P={P} config={config} testPrefix={testPrefix} />
        </section>
        <section id="billing"  data-module="billing"  className="scroll-mt-24">
          <BillingAutopilot P={P} config={config} testPrefix={testPrefix} />
        </section>
        <section id="growth"   data-module="growth"   className="scroll-mt-24">
          <GrowthEngine    P={P} config={config} testPrefix={testPrefix} />
        </section>

        <FounderNode P={P} testPrefix={testPrefix} />
      </main>

      {/* Amanda concierge pin is mounted GLOBALLY in App.js via
          <AmandaAvatar/> — appears here plus every other route. */}
    </div>
  );
}

// ============================================================
// Backdrop — vignette + subtle grid, tinted from palette
// ============================================================
function Backdrop({ P }) {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background:
            `radial-gradient(1100px 700px at 20% -10%, ${P.PRIMARY}29, transparent 60%),` +
            `radial-gradient(900px 500px at 100% 10%, ${P.ACCENT}1F, transparent 60%),` +
            `linear-gradient(180deg, ${P.BG} 0%, ${P.BG_HI} 55%, ${P.BG} 100%)`,
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage:
            `linear-gradient(${P.PRIMARY}3D 1px, transparent 1px),` +
            `linear-gradient(90deg, ${P.PRIMARY}3D 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 1100px 700px at 50% 30%, black 40%, transparent 90%)",
        }}
      />
      <style>{`
        @keyframes vc-ping {
          0%   { transform: scale(1);   opacity: .8; }
          70%  { transform: scale(1.9); opacity: 0;  }
          100% { transform: scale(1.9); opacity: 0;  }
        }
        @keyframes vc-shine {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// Hero Video
// ============================================================
function HeroVideo({ P, config, testPrefix }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const TradeIcon = tradeIcon(config.key);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <section data-testid={`${testPrefix}-hero-video`}>
      <div
        className="text-[10px] uppercase tracking-[0.32em] font-mono flex items-center gap-2 mb-3"
        style={{ color: P.ACCENT }}
      >
        <TradeIcon size={11} /> {config.hero.kicker}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white mb-4">
        Watch Amanda tell you what a{" "}
        <span style={{ color: P.PRIMARY }} className="italic">
          Grid Founder Node
        </span>{" "}
        actually does for your {config.hero.subject}.
      </h1>

      <div className="relative mx-auto w-full">
        <div
          className="absolute -inset-1 rounded-sm pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${P.ACCENT}55, ${P.PRIMARY}55, ${P.ACCENT}55)`,
            filter: "blur(18px)",
            opacity: 0.55,
          }}
        />
        <div
          className="relative rounded-sm overflow-hidden border"
          style={{
            borderColor: `${P.ACCENT}66`,
            boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 60px ${P.PRIMARY}22`,
            background: "#000",
          }}
        >
          <video
            ref={videoRef}
            src={HERO_VIDEO_URL}
            autoPlay
            muted
            playsInline
            controls
            data-testid={`${testPrefix}-hero-video-el`}
            className="w-full h-auto block bg-black"
          />
          <button
            type="button"
            onClick={toggleMute}
            data-testid={`${testPrefix}-hero-video-mute`}
            className="absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur transition-colors"
            style={{
              background: `${P.BG}BF`,
              border: `1px solid ${P.ACCENT}55`,
              color: P.ACCENT,
            }}
          >
            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
            <span className="text-[10px] uppercase tracking-[0.22em] font-mono">
              {muted ? "Tap to unmute" : "Muted off"}
            </span>
          </button>
          <div
            className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm backdrop-blur border"
            style={{ background: `${P.BG}BF`, borderColor: `${P.PRIMARY}55` }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}` }}
            />
            <span
              className="text-[9px] uppercase tracking-[0.22em] font-mono"
              style={{ color: P.INK }}
            >
              Amanda · Live · Founder Node
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Brand Stripe
// ============================================================
function BrandStripe({ P, config, testPrefix }) {
  const TradeIcon = tradeIcon(config.key);
  return (
    <section
      className="rounded-sm border p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
      style={{
        borderColor: `${P.PRIMARY}44`,
        background: `linear-gradient(90deg, ${P.PRIMARY}18 0%, ${P.BG}99 60%, ${P.ACCENT}0F 100%)`,
      }}
      data-testid={`${testPrefix}-brand-stripe`}
    >
      <div
        className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
        style={{
          borderColor: `${P.PRIMARY}80`,
          background: `${P.PRIMARY}22`,
          color: P.PRIMARY,
        }}
      >
        <TradeIcon size={20} />
      </div>
      <div className="flex-1">
        <div
          className="text-[10px] uppercase tracking-[0.32em] font-mono mb-1 flex flex-wrap items-center gap-x-3 gap-y-1"
          style={{ color: P.ACCENT }}
        >
          <span>{config.brand} · {config.location}</span>
          {config.leadArchitect && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border"
              style={{
                borderColor: `${P.PRIMARY}66`,
                background: `${P.PRIMARY}14`,
                color: P.PRIMARY,
              }}
              data-testid={`${testPrefix}-lead-architect`}
            >
              Lead Architect · <span style={{ color: P.INK }}>{config.leadArchitect}</span>
            </span>
          )}
        </div>
        <div className="font-display text-white text-lg sm:text-xl leading-tight">
          {config.brandStripe.title}{" "}
          <span style={{ color: P.PRIMARY }} className="italic">
            {config.brandStripe.titleTail}
          </span>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Section header + panel header (shared)
// ============================================================
function SectionBanner({ P, kicker, title, blurb, icon: Icon, rightPill, rightSlot }) {
  return (
    <div
      className="px-5 sm:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-b"
      style={{
        borderColor: "rgba(255,255,255,0.06)",
        background: `linear-gradient(90deg, ${P.PRIMARY}12 0%, ${P.BG}80 60%, ${P.ACCENT}10 100%)`,
      }}
    >
      <div
        className="w-10 h-10 rounded-sm border flex items-center justify-center shrink-0"
        style={{ borderColor: `${P.PRIMARY}66`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>{kicker}</div>
        <div className="font-display text-white text-lg sm:text-xl leading-tight mt-0.5">{title}</div>
        {blurb && (
          <p className="mt-2 text-[12px] sm:text-[13px] leading-relaxed max-w-3xl" style={{ color: P.MUTED }}>{blurb}</p>
        )}
      </div>
      {rightPill && (
        <span
          className="hidden md:inline text-[10px] uppercase tracking-[0.24em] font-mono px-3 py-1.5 rounded-sm shrink-0"
          style={{ background: `${P.ACCENT}12`, border: `1px solid ${P.ACCENT}55`, color: P.ACCENT }}
        >
          {rightPill}
        </span>
      )}
      {rightSlot && <div className="shrink-0">{rightSlot}</div>}
    </div>
  );
}

function PanelHeader({ P, kicker, title, icon: Icon, accent, right }) {
  const c = accent || P.PRIMARY;
  return (
    <div
      className="flex items-center justify-between px-4 sm:px-5 py-3 border-b"
      style={{
        borderColor: "rgba(255,255,255,0.06)",
        background: `linear-gradient(90deg, ${c}12 0%, ${P.BG}66 60%, ${P.BG}33 100%)`,
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-8 h-8 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: `${c}66`, background: `${c}14`, color: c }}
        >
          <Icon size={15} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: c }}>{kicker}</div>
          <div className="font-display text-white text-base sm:text-lg leading-tight truncate">{title}</div>
        </div>
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

// ============================================================
// 01 · INTAKE SHIELD (Booking Calendar + Call Me Later)
// ============================================================
function IntakeShield({ P, config, testPrefix }) {
  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}55`,
        background: `linear-gradient(180deg, ${P.PRIMARY}0F 0%, ${P.BG}F2 100%)`,
      }}
      data-testid={`${testPrefix}-intake-shield`}
    >
      <SectionBanner
        P={P}
        kicker={config.intakeShield.kicker}
        title={config.intakeShield.title}
        blurb={config.intakeShield.blurb}
        icon={ShieldCheck}
      />
      <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <BookingCalendar P={P} config={config} testPrefix={testPrefix} />
        </div>
        <div className="lg:col-span-2">
          <CallMeLater P={P} config={config} testPrefix={testPrefix} />
        </div>
      </div>
    </section>
  );
}

function BookingCalendar({ P, config, testPrefix }) {
  const b = config.intakeShield.booking;
  const [grid, setGrid] = useState(INITIAL_BOOKING_GRID);
  const [focused, setFocused] = useState(null);
  const openCount = useMemo(() => Object.values(grid).filter((v) => v === "open").length, [grid]);
  const bookedCount = useMemo(() => Object.values(grid).filter((v) => v === "crewA" || v === "crewB").length, [grid]);
  const requestedCount = useMemo(() => Object.values(grid).filter((v) => v === "requested").length, [grid]);

  const book = (cellKey) => {
    setGrid((g) => (g[cellKey] === "open" ? { ...g, [cellKey]: "requested" } : g));
    setFocused(cellKey);
    setTimeout(() => setFocused((f) => (f === cellKey ? null : f)), 4200);
  };

  return (
    <div
      className="rounded-sm border overflow-hidden"
      style={{
        borderColor: `${P.ACCENT}4D`,
        background: `linear-gradient(180deg, ${P.ACCENT}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid={`${testPrefix}-booking-calendar`}
    >
      <PanelHeader
        P={P}
        kicker={b.kicker}
        title={b.title}
        icon={CalIcon}
        accent={P.ACCENT}
        right={
          <div className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: P.MUTED }}>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}` }} />
              <span style={{ color: P.INK }}>{openCount}</span> open
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: P.ACCENT, boxShadow: `0 0 8px ${P.ACCENT}` }} />
              <span style={{ color: P.INK }}>{bookedCount}</span> booked
            </span>
            {requestedCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: P.INK }} />
                <span style={{ color: P.INK }}>{requestedCount}</span> pending
              </span>
            )}
          </div>
        }
      />
      <div className="p-4 sm:p-5">
        <div className="text-[11px] leading-relaxed mb-4" style={{ color: P.MUTED }}>
          {b.blurb}
        </div>

        <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}>
          <div />
          {CMD_DAYS.map((d) => (
            <div
              key={d.key}
              className="text-center rounded-sm border py-1.5"
              style={{ borderColor: `${P.PRIMARY}30`, background: `${P.PRIMARY}0D` }}
              data-testid={`${testPrefix}-booking-day-${d.key}`}
            >
              <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: P.PRIMARY }}>{d.label}</div>
              <div className="text-[9px] font-mono uppercase tracking-[0.16em] mt-0.5" style={{ color: P.MUTED }}>{d.date}</div>
            </div>
          ))}
        </div>

        {SLOTS.map((s) => {
          const SlotIcon = s.icon;
          return (
            <div key={s.key} className="grid gap-2 mb-2" style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}>
              <div
                className="rounded-sm border px-2 py-2 flex flex-col justify-center"
                style={{ borderColor: `${P.ACCENT}4D`, background: `${P.ACCENT}0F` }}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1" style={{ color: P.ACCENT }}>
                  <SlotIcon size={10} /> {s.label}
                </div>
                <div className="text-[9px] font-mono mt-0.5" style={{ color: P.MUTED }}>{s.sub}</div>
              </div>
              {CMD_DAYS.map((d) => {
                const cellKey = `${d.key}-${s.key}`;
                return (
                  <SlotCell
                    P={P}
                    config={config}
                    key={cellKey}
                    cellKey={cellKey}
                    state={grid[cellKey]}
                    focused={focused === cellKey}
                    onBook={() => book(cellKey)}
                    testId={`${testPrefix}-slot-${cellKey}`}
                  />
                );
              })}
            </div>
          );
        })}

        <div className="mt-4 text-[10px] uppercase tracking-[0.24em] font-mono flex items-center gap-2 flex-wrap" style={{ color: P.MUTED }}>
          <Users size={11} style={{ color: P.PRIMARY }} />
          {b.crewALabel} · <span style={{ color: P.ACCENT }}>booked</span> · {b.crewBLabel} · <span style={{ color: P.ACCENT }}>booked</span> · Open · <span style={{ color: P.PRIMARY }}>available</span>
        </div>
      </div>
    </div>
  );
}

function SlotCell({ P, config, cellKey, state, focused, onBook, testId }) {
  const b = config.intakeShield.booking;
  if (state === "crewA" || state === "crewB") {
    const label = state === "crewA" ? b.crewALabel : b.crewBLabel;
    return (
      <div
        className="rounded-sm border px-2 py-2 flex flex-col justify-center min-h-[74px]"
        style={{ borderColor: `${P.ACCENT}55`, background: `linear-gradient(160deg, ${P.ACCENT}18 0%, ${P.BG}99 60%)` }}
        data-testid={testId}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.ACCENT }}>{label}</div>
        <div className="text-[9px] font-mono uppercase tracking-[0.18em] mt-0.5" style={{ color: P.MUTED }}>· Booked</div>
      </div>
    );
  }
  if (state === "requested") {
    return (
      <div
        className="rounded-sm border px-2 py-2 flex flex-col justify-center min-h-[74px]"
        style={{
          borderColor: `${P.PRIMARY}80`,
          background: `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}8F 70%)`,
          boxShadow: focused ? `0 0 22px -4px ${P.PRIMARY}` : "none",
        }}
        data-testid={testId}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1" style={{ color: P.PRIMARY }}>
          <Check size={10} /> Requested
        </div>
        <div className="text-[9px] font-mono uppercase tracking-[0.16em] mt-0.5 leading-tight" style={{ color: P.INK }}>
          Amanda confirms shortly
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onBook}
      data-testid={testId}
      className="group rounded-sm border px-2 py-2 min-h-[74px] flex flex-col justify-center text-left transition-all"
      style={{ borderColor: `${P.PRIMARY}55`, background: `${P.PRIMARY}0F` }}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.PRIMARY }}>Open</div>
      <div
        className="text-[10px] uppercase tracking-[0.22em] font-mono mt-1 flex items-center gap-1 group-hover:translate-x-0.5 transition-transform"
        style={{ color: P.INK }}
      >
        {b.slotAction} <ArrowRight size={10} />
      </div>
    </button>
  );
}

function CallMeLater({ P, config, testPrefix }) {
  const c = config.intakeShield.callMeLater;
  const [step, setStep] = useState("idle");
  const [day, setDay] = useState("today");
  const [win, setWin] = useState("evening");
  const [phone, setPhone] = useState("");

  const chosenWin = CALLBACK_WINDOWS.find((w) => w.key === win) || CALLBACK_WINDOWS[2];
  const chosenDay = DAY_CHOICE.find((d) => d.key === day) || DAY_CHOICE[0];

  const readyToSend = phone.replace(/\D/g, "").length >= 7;
  const send = () => readyToSend && setStep("confirmed");
  const reset = () => { setStep("idle"); setPhone(""); };

  return (
    <div
      className="rounded-sm border overflow-hidden relative h-full flex flex-col"
      style={{
        borderColor: `${P.ACCENT}55`,
        background: `linear-gradient(180deg, ${P.ACCENT}12 0%, ${P.BG}F2 55%, ${P.PRIMARY}0F 100%)`,
        boxShadow: `0 24px 60px -24px ${P.ACCENT}55`,
      }}
      data-testid={`${testPrefix}-call-me-later`}
    >
      <PanelHeader P={P} kicker={c.kicker} title={c.title} icon={Phone} accent={P.ACCENT} />

      <div className="p-5 flex-1 flex flex-col">
        {step === "idle" && (
          <>
            <p className="text-sm leading-relaxed" style={{ color: P.INK }}>
              {c.idleCopy.split("Tap the button.")[0]}
              <span style={{ color: P.ACCENT }}>Tap the button.</span>
              {c.idleCopy.split("Tap the button.")[1] || ""}
            </p>
            <button
              type="button"
              onClick={() => setStep("picking")}
              data-testid={`${testPrefix}-cml-open`}
              className="mt-5 w-full flex items-center justify-center gap-3 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] transition-transform active:scale-[0.99]"
              style={{
                background: `linear-gradient(90deg, ${P.ACCENT_DEEP} 0%, ${P.ACCENT} 100%)`,
                border: `1px solid ${P.ACCENT}`, color: P.BG,
                boxShadow: `0 0 28px -6px ${P.ACCENT}90`,
              }}
            >
              <Phone size={16} /> Call Me Later
            </button>
            <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.MUTED }}>
              <Sparkles size={11} style={{ color: P.PRIMARY }} />
              Auto-scheduled · Text confirmation sent
            </div>
            <div className="mt-auto pt-6">
              <MiniProof P={P} config={config} testPrefix={testPrefix} />
            </div>
          </>
        )}

        {step === "picking" && (
          <div data-testid={`${testPrefix}-cml-picker`}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono mb-3" style={{ color: P.ACCENT }}>Pick your window</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {DAY_CHOICE.map((d) => {
                const active = day === d.key;
                return (
                  <button
                    key={d.key} type="button" onClick={() => setDay(d.key)}
                    className="rounded-sm border py-2 px-3 text-left transition-all"
                    style={{
                      borderColor: active ? P.PRIMARY : "rgba(255,255,255,0.12)",
                      background: active ? `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}80 70%)` : `${P.BG}99`,
                      boxShadow: active ? `0 0 18px -6px ${P.PRIMARY}` : "none",
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: active ? P.PRIMARY : P.MUTED }}>{d.label}</div>
                    <div className="text-[9px] font-mono mt-0.5" style={{ color: P.MUTED }}>{d.sub}</div>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {CALLBACK_WINDOWS.map((w) => {
                const active = win === w.key;
                const Icon = w.icon;
                return (
                  <button
                    key={w.key} type="button" onClick={() => setWin(w.key)}
                    className="rounded-sm border py-3 px-2 text-center transition-all"
                    style={{
                      borderColor: active ? P.ACCENT : "rgba(255,255,255,0.12)",
                      background: active ? `linear-gradient(160deg, ${P.ACCENT}22 0%, ${P.BG}80 70%)` : `${P.BG}99`,
                      boxShadow: active ? `0 0 18px -6px ${P.ACCENT}` : "none",
                    }}
                  >
                    <Icon size={16} className="mx-auto" style={{ color: active ? P.ACCENT : P.MUTED }} />
                    <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: active ? P.ACCENT : P.INK }}>{w.label}</div>
                    <div className="text-[9px] font-mono" style={{ color: P.MUTED }}>{w.sub}</div>
                  </button>
                );
              })}
            </div>
            <label className="block text-[10px] uppercase tracking-[0.24em] font-mono mb-1.5" style={{ color: P.ACCENT }}>
              <MessageSquare size={11} className="inline mr-1" /> {c.contactLabel}
            </label>
            <input
              type="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(614) 555-0142"
              data-testid={`${testPrefix}-cml-phone`}
              className="w-full bg-black/60 border rounded-sm px-4 py-3 text-white text-sm focus:outline-none min-h-[44px] transition-colors"
              style={{ borderColor: readyToSend ? `${P.PRIMARY}80` : "rgba(255,255,255,0.12)" }}
              maxLength={40} autoComplete="tel"
            />
            <button
              type="button" onClick={send} disabled={!readyToSend}
              data-testid={`${testPrefix}-cml-send`}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 rounded-sm text-sm font-display uppercase tracking-[0.24em] disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.99]"
              style={{
                background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
                border: `1px solid ${P.PRIMARY}`, color: P.BG,
                boxShadow: readyToSend ? `0 0 26px -6px ${P.PRIMARY}90` : "none",
              }}
            >
              <ArrowRight size={14} /> Send My Callback Request
            </button>
            <button type="button" onClick={() => setStep("idle")} className="mt-3 w-full text-[10px] uppercase tracking-[0.24em] font-mono py-2" style={{ color: P.MUTED }}>← Back</button>
          </div>
        )}

        {step === "confirmed" && (
          <div
            className="rounded-sm border p-5 relative overflow-hidden flex-1 flex flex-col justify-center"
            style={{
              borderColor: P.PRIMARY,
              background: `linear-gradient(160deg, ${P.PRIMARY}22 0%, ${P.BG}B3 60%, ${P.ACCENT}12 100%)`,
              boxShadow: `0 0 34px -8px ${P.PRIMARY}`,
            }}
            data-testid={`${testPrefix}-cml-confirmed`}
          >
            <div className="flex items-center gap-3">
              <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-full" style={{ background: P.PRIMARY, color: P.BG }}>
                <Check size={22} strokeWidth={3} />
                <span className="absolute inset-0 rounded-full" style={{ background: P.PRIMARY, animation: "vc-ping 2s cubic-bezier(0,0,0.2,1) infinite" }} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: P.PRIMARY }}>Got it — you&apos;re on the book</div>
                <div className="font-display text-white text-lg leading-tight mt-0.5">
                  Amanda will call you {chosenDay.label.toLowerCase()}, <span style={{ color: P.ACCENT }}>{chosenWin.label.toLowerCase()}</span>.
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: P.INK }}>
              A confirmation text is on its way to <span style={{ color: P.ACCENT }}>{phone || "your phone"}</span>. If anything changes, reply to that text and Amanda re-books on your side of the line.
            </p>
            <div className="mt-4 rounded-sm border p-3 flex items-start gap-3" style={{ borderColor: "rgba(255,255,255,0.10)", background: `${P.BG}A6` }}>
              <MessageSquare size={14} style={{ color: P.PRIMARY }} className="mt-0.5" />
              <div className="text-[12px] leading-relaxed" style={{ color: P.MUTED }}>
                <span style={{ color: P.INK }}>{c.brandLine}</span> Locked you in for {chosenDay.label.toLowerCase()} {chosenWin.sub}. I&apos;ll call from <span style={{ color: P.ACCENT }}>{c.callFrom}</span>. Reply STOP to cancel.
              </div>
            </div>
            <button type="button" onClick={reset} className="mt-5 self-start text-[10px] uppercase tracking-[0.24em] font-mono px-3 py-2 rounded-sm border" style={{ borderColor: "rgba(255,255,255,0.14)", color: P.INK, background: `${P.BG}A6` }}>
              Book Another Window <ChevronRight size={11} className="inline" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MiniProof({ P, config, testPrefix }) {
  const proof = config.intakeShield.proof;
  return (
    <div className="rounded-sm border p-3" style={{ borderColor: `${P.PRIMARY}4D`, background: `${P.PRIMARY}0F` }} data-testid={`${testPrefix}-mini-proof`}>
      <div className="text-[10px] uppercase tracking-[0.28em] font-mono mb-2 flex items-center gap-2" style={{ color: P.PRIMARY }}>
        <ShieldCheck size={11} /> {proof.title}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {proof.stats.map((s) => (
          <div key={s.k}>
            <div className="font-display text-white text-lg leading-none" style={{ color: s.v === "0" ? P.PRIMARY : P.INK }}>{s.v}</div>
            <div className="text-[9px] font-mono uppercase tracking-[0.22em] mt-0.5" style={{ color: P.MUTED }}>{s.k}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// 02 · COMMAND CALENDAR
// ============================================================
function CommandCalendar({ P, config, testPrefix }) {
  const cc = config.commandCalendar;
  const CREWS = useMemo(() => ([
    { key: "A", label: config.intakeShield.booking.crewALabel, tone: P.ACCENT },
    { key: "B", label: config.intakeShield.booking.crewBLabel, tone: P.PRIMARY },
  ]), [config, P]);
  const [jobs, setJobs] = useState(cc.jobs);
  const [formOpen, setFormOpen] = useState(false);
  const [flash, setFlash] = useState(null);
  const [draft, setDraft] = useState({ day: "mon", time: "8am", crew: "A", addr: "", svc: "" });

  const cellFor = (day, time) => jobs.find((j) => j.day === day && j.time === time);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!draft.addr.trim() || !draft.svc.trim()) return;
    const id = `j-${Date.now()}`;
    setJobs((js) => [...js, { ...draft, id, addr: draft.addr.trim(), svc: draft.svc.trim() }]);
    setFlash(id);
    setTimeout(() => setFlash((f) => (f === id ? null : f)), 3500);
    setFormOpen(false);
    setDraft({ day: "mon", time: "8am", crew: "A", addr: "", svc: "" });
  };

  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.PRIMARY}44`,
        background: `linear-gradient(180deg, ${P.PRIMARY}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid={`${testPrefix}-command-calendar`}
    >
      <SectionBanner
        P={P}
        kicker={cc.kicker}
        title={cc.title}
        blurb={cc.blurb}
        icon={Truck}
        rightSlot={
          <button
            type="button"
            onClick={() => setFormOpen((v) => !v)}
            data-testid={`${testPrefix}-cmd-add-job`}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
            style={{
              background: `linear-gradient(90deg, ${P.ACCENT_DEEP} 0%, ${P.ACCENT} 100%)`,
              border: `1px solid ${P.ACCENT}`, color: P.BG,
              boxShadow: `0 0 20px -8px ${P.ACCENT}`,
            }}
          >
            <Plus size={13} /> {cc.addAction}
          </button>
        }
      />

      <div className="p-5 sm:p-6">
        {formOpen && (
          <form
            onSubmit={submit}
            data-testid={`${testPrefix}-cmd-form`}
            className="mb-5 rounded-sm border p-4"
            style={{
              borderColor: `${P.ACCENT}66`,
              background: `linear-gradient(180deg, ${P.ACCENT}10 0%, ${P.BG}E6 100%)`,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: P.ACCENT }}>
                <Plus size={11} /> {cc.newLabel}
              </div>
              <button type="button" onClick={() => setFormOpen(false)} style={{ color: P.MUTED }}>
                <XIcon size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <FieldSelect P={P} label="Day" value={draft.day} onChange={(v) => setDraft({ ...draft, day: v })}
                options={CMD_DAYS.map((d) => ({ v: d.key, l: `${d.label} · ${d.date}` }))} />
              <FieldSelect P={P} label="Time" value={draft.time} onChange={(v) => setDraft({ ...draft, time: v })}
                options={TIME_BLOCKS.map((t) => ({ v: t, l: t }))} />
              <FieldSelect P={P} label="Crew" value={draft.crew} onChange={(v) => setDraft({ ...draft, crew: v })}
                options={CREWS.map((c) => ({ v: c.key, l: c.label }))} />
              <FieldInput P={P} label={cc.addrLabel} value={draft.addr} onChange={(v) => setDraft({ ...draft, addr: v })} placeholder={cc.addrPlaceholder} />
              <FieldInput P={P} label={cc.svcLabel}  value={draft.svc}  onChange={(v) => setDraft({ ...draft, svc: v })}  placeholder={cc.svcPlaceholder} />
            </div>
            <button
              type="submit"
              disabled={!draft.addr.trim() || !draft.svc.trim()}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-display uppercase tracking-[0.24em] disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.99]"
              style={{
                background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
                border: `1px solid ${P.PRIMARY}`, color: P.BG,
                boxShadow: `0 0 20px -8px ${P.PRIMARY}`,
              }}
            >
              <Send size={14} /> Add to Dispatch Board
            </button>
          </form>
        )}

        <div className="overflow-x-auto">
          <div className="min-w-[820px]">
            <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}>
              <div />
              {CMD_DAYS.map((d) => (
                <div
                  key={d.key}
                  className="text-center rounded-sm border py-1.5"
                  style={{ borderColor: `${P.PRIMARY}33`, background: `${P.PRIMARY}0F` }}
                >
                  <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: P.PRIMARY }}>{d.label}</div>
                  <div className="text-[9px] font-mono uppercase tracking-[0.16em] mt-0.5" style={{ color: P.MUTED }}>{d.date}</div>
                </div>
              ))}
            </div>

            {TIME_BLOCKS.map((t) => (
              <div key={t} className="grid gap-2 mb-2" style={{ gridTemplateColumns: "80px repeat(7, 1fr)" }}>
                <div
                  className="rounded-sm border px-2 py-2 flex flex-col justify-center"
                  style={{ borderColor: `${P.ACCENT}33`, background: `${P.ACCENT}0A` }}
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1" style={{ color: P.ACCENT }}>
                    <Clock size={10} /> {t}
                  </div>
                </div>
                {CMD_DAYS.map((d) => {
                  const job = cellFor(d.key, t);
                  return (
                    <CmdCell
                      P={P} config={config}
                      key={`${d.key}-${t}`}
                      job={job}
                      flash={flash === job?.id}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: P.MUTED }}>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: P.ACCENT, boxShadow: `0 0 8px ${P.ACCENT}` }} />
            <span style={{ color: P.ACCENT }}>{CREWS[0].label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}` }} />
            <span style={{ color: P.PRIMARY }}>{CREWS[1].label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full border" style={{ borderColor: P.MUTED }} />
            <span>Open</span>
          </div>
          <span className="ml-auto">{cc.footerCount(jobs.length)}</span>
        </div>
      </div>
    </section>
  );
}

function CmdCell({ P, config, job, flash }) {
  if (!job) {
    return (
      <div
        className="rounded-sm border px-2 py-2 min-h-[64px] flex items-center justify-center"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: `${P.BG}66` }}
      >
        <span className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.MUTED }}>Open</span>
      </div>
    );
  }
  const tone = job.crew === "A" ? P.ACCENT : P.PRIMARY;
  const crewLabel = job.crew === "A" ? config.intakeShield.booking.crewALabel : config.intakeShield.booking.crewBLabel;
  return (
    <div
      className="rounded-sm border px-2 py-2 min-h-[64px] flex flex-col justify-center"
      style={{
        borderColor: `${tone}80`,
        background: `linear-gradient(160deg, ${tone}22 0%, ${P.BG}99 65%)`,
        boxShadow: flash ? `0 0 22px -4px ${tone}` : "none",
      }}
    >
      <div className="text-[9px] uppercase tracking-[0.22em] font-mono flex items-center gap-1" style={{ color: tone }}>
        <Users size={9} /> {crewLabel}
      </div>
      <div className="text-[11px] leading-tight text-white font-display truncate mt-0.5" title={job.addr}>{job.addr}</div>
      <div className="text-[9px] font-mono mt-0.5 truncate" style={{ color: P.MUTED }}>{job.svc}</div>
    </div>
  );
}

function FieldSelect({ P, label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.24em] font-mono mb-1" style={{ color: P.ACCENT }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/60 border rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none min-h-[42px]"
        style={{ borderColor: `${P.ACCENT}59` }}
      >
        {options.map((o) => (
          <option key={o.v} value={o.v} style={{ background: P.BG, color: P.INK }}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}
function FieldInput({ P, label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.24em] font-mono mb-1" style={{ color: P.ACCENT }}>{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black/60 border rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none min-h-[42px]"
        style={{ borderColor: `${P.ACCENT}59` }}
      />
    </label>
  );
}

// ============================================================
// 03 · UTILITY SPINE (Client / Fan / Rider / Broker / Owner notes)
// ============================================================
function UtilitySpine({ P, config, testPrefix }) {
  const sp = config.spine;
  const [files, setFiles] = useState(sp.files);
  const [formOpen, setFormOpen] = useState(false);
  const [flash, setFlash] = useState(null);
  const [draft, setDraft] = useState({ name: "", addr: "", note: "" });

  const submit = (e) => {
    e?.preventDefault?.();
    if (!draft.name.trim() || !draft.addr.trim() || !draft.note.trim()) return;
    const id = `f-${Date.now()}`;
    setFiles((cs) => [{ id, name: draft.name.trim(), addr: draft.addr.trim(), last: "New file", next: "Amanda schedules", note: draft.note.trim() }, ...cs]);
    setFlash(id);
    setTimeout(() => setFlash((f) => (f === id ? null : f)), 3500);
    setFormOpen(false);
    setDraft({ name: "", addr: "", note: "" });
  };

  return (
    <section
      className="rounded-sm border overflow-hidden"
      style={{
        borderColor: `${P.PRIMARY}44`,
        background: `linear-gradient(180deg, ${P.PRIMARY}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid={`${testPrefix}-utility-spine`}
    >
      <SectionBanner
        P={P}
        kicker={sp.kicker}
        title={sp.title}
        blurb={sp.blurb}
        icon={ClipboardList}
        rightSlot={
          <button
            type="button"
            onClick={() => setFormOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
            style={{
              background: `linear-gradient(90deg, ${P.ACCENT_DEEP} 0%, ${P.ACCENT} 100%)`,
              border: `1px solid ${P.ACCENT}`, color: P.BG,
              boxShadow: `0 0 20px -8px ${P.ACCENT}`,
            }}
          >
            <Plus size={13} /> {sp.addAction}
          </button>
        }
      />

      <div className="p-5 sm:p-6 space-y-3">
        {formOpen && (
          <form
            onSubmit={submit}
            className="rounded-sm border p-4"
            style={{
              borderColor: `${P.ACCENT}66`,
              background: `linear-gradient(180deg, ${P.ACCENT}10 0%, ${P.BG}E6 100%)`,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: P.ACCENT }}>
                <Plus size={11} /> {sp.newFileLabel}
              </div>
              <button type="button" onClick={() => setFormOpen(false)} style={{ color: P.MUTED }}>
                <XIcon size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <FieldInput P={P} label={sp.entityLabel} value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="" />
              <FieldInput P={P} label={sp.addrLabel}   value={draft.addr} onChange={(v) => setDraft({ ...draft, addr: v })} placeholder="" />
            </div>
            <label className="block mt-2">
              <span className="block text-[10px] uppercase tracking-[0.24em] font-mono mb-1" style={{ color: P.ACCENT }}>Note</span>
              <textarea
                value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                rows={3}
                className="w-full bg-black/60 border rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none"
                style={{ borderColor: `${P.ACCENT}59` }}
                maxLength={280}
              />
            </label>
            <button
              type="submit"
              disabled={!draft.name.trim() || !draft.addr.trim() || !draft.note.trim()}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-sm text-sm font-display uppercase tracking-[0.24em] disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.99]"
              style={{
                background: `linear-gradient(90deg, ${P.PRIMARY_DEEP} 0%, ${P.PRIMARY} 100%)`,
                border: `1px solid ${P.PRIMARY}`, color: P.BG,
                boxShadow: `0 0 20px -8px ${P.PRIMARY}`,
              }}
            >
              <Send size={14} /> Save
            </button>
          </form>
        )}

        {files.map((c) => (
          <div
            key={c.id}
            className="rounded-sm border p-4"
            style={{
              borderColor: flash === c.id ? P.PRIMARY : `${P.PRIMARY}47`,
              background: flash === c.id
                ? `linear-gradient(160deg, ${P.PRIMARY}18 0%, ${P.BG}B3 60%)`
                : `${P.BG}A6`,
              boxShadow: flash === c.id ? `0 0 22px -6px ${P.PRIMARY}` : "none",
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
                style={{ borderColor: `${P.PRIMARY}80`, background: `${P.PRIMARY}18`, color: P.PRIMARY }}
              >
                <HomeIcon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <div className="font-display text-white text-base leading-tight">{c.name}</div>
                  <span className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.ACCENT }}>
                    <MapPin size={10} className="inline mr-1" />{c.addr}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.MUTED }}>
                    Last · <span style={{ color: P.INK }}>{c.last}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: P.MUTED }}>
                    Next · <span style={{ color: P.PRIMARY }}>{c.next}</span>
                  </div>
                </div>
                <div
                  className="mt-3 rounded-sm border-l-2 pl-3 py-1.5 text-[12px] leading-relaxed"
                  style={{ borderColor: P.ACCENT, background: `${P.ACCENT}0F`, color: P.INK }}
                >
                  {c.note}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div
          className="mt-2 rounded-sm border px-4 py-3 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.22em] font-mono"
          style={{ borderColor: "rgba(255,255,255,0.10)", background: `${P.BG}A6`, color: P.MUTED }}
        >
          <span>{sp.counterLabel} · <span style={{ color: P.PRIMARY }}>{files.length}</span></span>
          <span>Notes searchable · <span style={{ color: P.ACCENT }}>every field</span></span>
          <span className="ml-auto">Voice-to-text · Field phones · ON</span>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 04 · BILLING AUTOPILOT
// ============================================================
function BillingAutopilot({ P, config, testPrefix }) {
  const bl = config.billing;
  const initial = useMemo(() => bl.invoices.map((i) => ({ ...i, status: "open" })), [bl]);
  const [jobs, setJobs] = useState(initial);
  const [flash, setFlash] = useState(null);

  const send = (id) => {
    setJobs((js) => js.map((j) => (j.id === id ? { ...j, status: "invoiced" } : j)));
    setFlash(id);
    setTimeout(() => setFlash((f) => (f === id ? null : f)), 3500);
  };

  const openCount = jobs.filter((j) => j.status === "open").length;
  const invoicedCount = jobs.filter((j) => j.status === "invoiced").length;
  const invoicedTotal = jobs.filter((j) => j.status === "invoiced").reduce((s, j) => s + j.price, 0);

  return (
    <section
      className="rounded-sm border overflow-hidden"
      style={{
        borderColor: `${P.ACCENT}44`,
        background: `linear-gradient(180deg, ${P.ACCENT}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid={`${testPrefix}-billing-autopilot`}
    >
      <SectionBanner
        P={P}
        kicker={bl.kicker}
        title={bl.title}
        blurb={bl.blurb}
        icon={DollarSign}
        rightPill={`${openCount} waiting · $${invoicedTotal} billed today`}
      />

      <div className="p-5 sm:p-6 space-y-3">
        {jobs.map((j) => {
          const isOpen = j.status === "open";
          const isFlash = flash === j.id;
          return (
            <div
              key={j.id}
              className="rounded-sm border p-4 flex flex-col md:flex-row md:items-center gap-4 transition-all"
              style={{
                borderColor: isOpen ? `${P.ACCENT}44` : `${P.PRIMARY}66`,
                background: isOpen ? `${P.BG}B3` : `linear-gradient(160deg, ${P.PRIMARY}18 0%, ${P.BG}B3 60%)`,
                boxShadow: isFlash ? `0 0 26px -6px ${P.PRIMARY}` : "none",
              }}
              data-testid={`${testPrefix}-billing-job-${j.id}`}
            >
              <div
                className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
                style={{
                  borderColor: isOpen ? `${P.ACCENT}80` : `${P.PRIMARY}80`,
                  background: isOpen ? `${P.ACCENT}18` : `${P.PRIMARY}22`,
                  color: isOpen ? P.ACCENT : P.PRIMARY,
                }}
              >
                {isOpen ? <FileText size={18} /> : <Check size={18} strokeWidth={3} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-2" style={{ color: isOpen ? P.ACCENT : P.PRIMARY }}>
                  <MapPin size={11} /> {j.addr}
                </div>
                <div className="font-display text-white text-base sm:text-lg leading-tight mt-0.5 truncate">{j.svc}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono mt-0.5" style={{ color: P.MUTED }}>
                  {j.crew} · {j.date} · ${j.price}
                </div>
              </div>
              <div className="flex flex-col items-stretch md:items-end gap-1.5 shrink-0">
                {isOpen ? (
                  <button
                    type="button"
                    onClick={() => send(j.id)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono transition-transform active:scale-[0.98]"
                    style={{
                      background: `linear-gradient(90deg, ${P.ACCENT_DEEP} 0%, ${P.ACCENT} 100%)`,
                      border: `1px solid ${P.ACCENT}`, color: P.BG,
                      boxShadow: `0 0 20px -8px ${P.ACCENT}`,
                    }}
                  >
                    <Send size={13} /> Send Invoice
                  </button>
                ) : (
                  <div
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-[11px] uppercase tracking-[0.24em] font-mono"
                    style={{ background: `${P.PRIMARY}22`, border: `1px solid ${P.PRIMARY}`, color: P.PRIMARY }}
                  >
                    <FileText size={13} /> Invoice Sent · Text Delivered
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div
          className="mt-2 rounded-sm border px-4 py-3 flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.22em] font-mono"
          style={{ borderColor: "rgba(255,255,255,0.10)", background: `${P.BG}A6`, color: P.MUTED }}
        >
          <span>{bl.openLabel} · <span style={{ color: P.ACCENT }}>{openCount}</span></span>
          <span>{bl.invoicedLabel} · <span style={{ color: P.PRIMARY }}>{invoicedCount}</span></span>
          <span>{bl.billedLabel} · <span style={{ color: P.PRIMARY }}>${invoicedTotal}</span></span>
          <span className="ml-auto">Stripe · Auto-collect ON</span>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 05 · GROWTH ENGINE
// ============================================================
const FB_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ------------------------------------------------------------
// DemoVideoCarousel — Cinematic 10-clip reel mounted inside
// GrowthEngine for every vertical. Sources from DEMO_VIDEOS in
// AssetSpine — one edit propagates to all 12 routes.
// ------------------------------------------------------------
function DemoVideoCarousel({ P }) {
  const [active, setActive] = useState(0);
  return (
    <div className="mt-6" data-testid="demo-video-carousel">
      <p
        className="text-center text-sm font-semibold mb-4"
        style={{ color: P.PRIMARY }}
      >
        This is what your 10 weekly assets look like.
      </p>
      {/* Main video player */}
      <div
        className="relative rounded-xl overflow-hidden bg-black"
        style={{ aspectRatio: "16/9" }}
      >
        <video
          key={DEMO_VIDEOS[active].url}
          src={DEMO_VIDEOS[active].url}
          controls
          autoPlay={false}
          className="w-full h-full object-cover"
          style={{ display: "block" }}
          data-testid={`demo-video-main-${active}`}
        />
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
          {active + 1} / {DEMO_VIDEOS.length} · {DEMO_VIDEOS[active].title}
        </div>
      </div>
      {/* Horizontal scroll thumbnail rail */}
      <div className="flex gap-2 overflow-x-auto pb-2 mt-3 snap-x snap-mandatory">
        {DEMO_VIDEOS.map((v, i) => (
          <button
            key={v.id}
            onClick={() => setActive(i)}
            className="flex-shrink-0 snap-start rounded-lg overflow-hidden border-2 transition-all"
            style={{
              width: 88,
              height: 56,
              borderColor: i === active ? P.PRIMARY : "transparent",
              opacity: i === active ? 1 : 0.6,
            }}
            data-testid={`demo-video-thumb-${i}`}
          >
            <video
              src={v.url}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover pointer-events-none"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function GrowthEngine({ P, config, testPrefix }) {
  const g = config.growth;
  const iconFor = (i) => [Sparkles, Star, Users, Zap, Camera, VideoIcon][i % 6];
  const toneFor = (i) => (i % 2 === 0 ? P.PRIMARY : P.ACCENT);

  const bydayCount = useMemo(() => {
    const m = {};
    g.videos.forEach((v) => { m[v.day] = (m[v.day] || 0) + 1; });
    return m;
  }, [g.videos]);

  return (
    <section
      className="rounded-sm border overflow-hidden"
      style={{
        borderColor: `${P.ACCENT}44`,
        background: `linear-gradient(180deg, ${P.ACCENT}0D 0%, ${P.BG}F2 100%)`,
      }}
      data-testid={`${testPrefix}-growth-engine`}
    >
      <SectionBanner
        P={P}
        kicker={g.kicker}
        title={g.title}
        blurb={g.blurb}
        icon={VideoIcon}
        rightPill={g.pill}
      />

      {g.videoShowcase && (
        <div className="px-5 sm:px-6 pt-5 sm:pt-6" data-testid={`${testPrefix}-growth-showcase`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {g.videoShowcase.videos.map((clip, i) => (
              <figure
                key={`showcase-${i}`}
                className="rounded-sm border overflow-hidden"
                style={{
                  borderColor: `${P.PRIMARY}55`,
                  background: `linear-gradient(160deg, ${P.PRIMARY}14 0%, ${P.BG}E6 70%)`,
                  boxShadow: `0 20px 40px -22px ${P.PRIMARY_DEEP}88, inset 0 1px 0 ${P.ACCENT}18`,
                }}
                data-testid={`${testPrefix}-growth-showcase-clip-${i}`}
              >
                <video
                  src={clip.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                  className="w-full aspect-video bg-black"
                  data-testid={`${testPrefix}-growth-showcase-video-${i}`}
                />
                <figcaption
                  className="px-4 py-3 border-t flex items-center gap-2"
                  style={{
                    borderColor: `${P.ACCENT}22`,
                    background: `${P.BG_HI}CC`,
                  }}
                >
                  <VideoIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: P.PRIMARY }} />
                  <span
                    className="text-sm font-medium leading-snug"
                    style={{ color: P.INK }}
                  >
                    {clip.label}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>

          {g.videoShowcase.caption && (
            <p
              className="mt-5 sm:mt-6 text-center text-[13px] sm:text-sm italic tracking-wide"
              style={{ color: P.MUTED }}
              data-testid={`${testPrefix}-growth-showcase-caption`}
            >
              {g.videoShowcase.caption}
            </p>
          )}
        </div>
      )}

      {/* Cinematic demo reel · 10 clips */}
      <div className="px-5 sm:px-6">
        <DemoVideoCarousel P={P} />
      </div>

      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-7 gap-2 mb-5">
          {FB_DAYS.map((d) => (
            <div
              key={d}
              className="rounded-sm border py-2 px-1 text-center"
              style={{ borderColor: `${P.ACCENT}44`, background: `${P.ACCENT}0F` }}
            >
              <div className="text-[10px] uppercase tracking-[0.24em] font-mono" style={{ color: P.ACCENT }}>{d}</div>
              <div className="font-display text-lg leading-none mt-1" style={{ color: (bydayCount[d] || 0) > 0 ? P.PRIMARY : P.MUTED }}>
                {(bydayCount[d] || 0)}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] mt-0.5" style={{ color: P.MUTED }}>posts</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {g.videos.map((v, i) => {
            const Icon = iconFor(i);
            const tone = toneFor(i);
            return (
              <div
                key={`fb-${i}`}
                className="rounded-sm border overflow-hidden group"
                style={{
                  borderColor: `${tone}55`,
                  background: `linear-gradient(160deg, ${tone}14 0%, ${P.BG}D9 65%)`,
                }}
                data-testid={`${testPrefix}-growth-post-${i}`}
              >
                <div className="relative h-24 overflow-hidden border-b" style={{ borderColor: "rgba(255,255,255,0.05)", background: `${P.BG}80` }}>
                  <svg viewBox="0 0 300 96" className="w-full h-full">
                    <defs>
                      <linearGradient id={`${testPrefix}-fb-thumb-${i}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={tone} stopOpacity="0.35" />
                        <stop offset="100%" stopColor={tone} stopOpacity="0.05" />
                      </linearGradient>
                    </defs>
                    <rect width="300" height="96" fill={`url(#${testPrefix}-fb-thumb-${i})`} />
                    <g stroke={tone} strokeWidth="1.1" opacity="0.55">
                      {[...Array(24)].map((_, k) => {
                        const x = 8 + k * 12 + ((i + k) % 3);
                        return (
                          <path
                            key={k}
                            d={`M ${x} 90 L ${x - 2} 74 L ${x} 70 L ${x + 2} 74 Z`}
                            fill={tone}
                            fillOpacity="0.4"
                          />
                        );
                      })}
                    </g>
                    <circle cx="150" cy="42" r="18" fill="none" stroke={tone} strokeWidth="1.2" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(10,10,10,0.15)" }}>
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `${P.BG}CC`, border: `1px solid ${tone}88`, color: tone }}
                    >
                      <Play size={16} />
                    </div>
                  </div>
                  <div
                    className="absolute top-2 left-2 px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.24em] font-mono border"
                    style={{ borderColor: `${tone}80`, color: tone, background: `${P.BG}D9` }}
                  >
                    <Icon size={10} className="inline mr-1" /> {v.type}
                  </div>
                  <div className="absolute bottom-2 right-2 text-[9px] font-mono uppercase tracking-[0.22em]" style={{ color: P.MUTED }}>{v.day} · {v.slot}</div>
                </div>
                <div className="p-3">
                  <div className="font-display text-white text-sm leading-tight">{v.title}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-1.5" style={{ color: tone }}>
                    Drafted · Scheduled <ChevronRight size={11} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 rounded-sm border px-4 py-3 flex items-center gap-3" style={{ borderColor: `${P.PRIMARY}44`, background: `${P.PRIMARY}0F` }}>
          <Sparkles size={14} style={{ color: P.PRIMARY }} />
          <div className="text-[11px] leading-relaxed" style={{ color: P.INK }}>
            <span className="uppercase tracking-[0.22em] font-mono mr-2" style={{ color: P.PRIMARY }}>Engine ·</span>
            Amanda pulls raw footage from the field, drafts captions, picks the timing, and pushes to your channels — you approve in one tap.
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// FOUNDER NODE + 1-2-12
// ============================================================
function FounderNode({ P, testPrefix }) {
  const location = useLocation();
  // Slug = current path without the leading "/", e.g. "green-shield".
  // Passed to /induction so the success view can route users back
  // to the vertical they came from (via useSearchParams on Induction).
  const verticalSlug = location.pathname.replace(/^\/+/, "").replace(/\/.*$/, "");
  const inductionHref = verticalSlug
    ? `${FOUNDER_CTA_URL}?vertical=${verticalSlug}`
    : FOUNDER_CTA_URL;

  return (
    <section
      className="rounded-sm border overflow-hidden relative"
      style={{
        borderColor: `${P.ACCENT}88`,
        background: `linear-gradient(160deg, ${P.ACCENT}22 0%, ${P.BG}D9 45%, ${P.PRIMARY}18 100%)`,
        boxShadow: `0 40px 100px -30px ${P.ACCENT}66, 0 0 0 1px ${P.ACCENT}22 inset`,
      }}
      data-testid={`${testPrefix}-founder-node`}
    >
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.ACCENT}, ${P.PRIMARY}, ${P.ACCENT}, transparent)` }} />
      <div
        className="absolute inset-y-0 w-40 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${P.ACCENT}22, transparent)`,
          animation: "vc-shine 4.6s ease-in-out infinite",
        }}
      />

      <div className="relative p-6 sm:p-10">
        <div className="text-[10px] uppercase tracking-[0.36em] font-mono flex items-center gap-2 mb-3" style={{ color: P.ACCENT }}>
          <Trophy size={12} /> Founder Node
        </div>

        <div
          className="inline-flex items-center gap-3 rounded-sm border px-4 sm:px-5 py-3 mb-6"
          style={{
            borderColor: P.ACCENT,
            background: `linear-gradient(90deg, ${P.ACCENT_DEEP}18 0%, ${P.ACCENT}22 60%, ${P.PRIMARY}18 100%)`,
            boxShadow: `0 0 24px -4px ${P.ACCENT}80`,
          }}
        >
          <div
            className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
            style={{ borderColor: P.ACCENT, background: `${P.ACCENT}30`, color: P.ACCENT }}
          >
            <Award size={17} />
          </div>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.32em] font-mono" style={{ color: P.PRIMARY }}>{COPY.FOUNDER_KICKER}</div>
            <div className="font-display text-white text-base sm:text-lg leading-tight">
              {COPY.FOUNDER_BADGE_LINE}
            </div>
          </div>
        </div>

        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-tight max-w-3xl">
          The <span style={{ color: P.ACCENT }} className="italic">1-2-12 Founder Terms</span> — three doors, one lifetime rate.
        </h2>
        <p className="mt-3 text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: P.MUTED }}>
          Month one is on the house — we build and configure everything. Month
          two you soft-launch. From month twelve on you&apos;re paying the
          founder rate — and that rate never rises. Ever.
        </p>

        <PricingTimeline P={P} testPrefix={testPrefix} />

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <Link
            to={inductionHref}
            data-testid={`${testPrefix}-founder-cta`}
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-sm font-display uppercase tracking-[0.24em] transition-transform active:scale-[0.99]"
            style={{
              background: `linear-gradient(90deg, ${P.ACCENT_DEEP} 0%, ${P.ACCENT} 100%)`,
              border: `1px solid ${P.ACCENT}`, color: P.BG,
              boxShadow: `0 0 32px -6px ${P.ACCENT}`,
            }}
          >
            <Trophy size={15} /> {COPY.FOUNDER_CTA_LABEL} <ArrowRight size={15} />
          </Link>
          <div className="text-[10px] uppercase tracking-[0.24em] font-mono flex items-center gap-2" style={{ color: P.MUTED }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.PRIMARY, boxShadow: `0 0 8px ${P.PRIMARY}` }} />
            Only 12 Founder seats left · Q1 2026
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingTimeline({ P, testPrefix }) {
  return (
    <div
      className="mt-6 rounded-sm border overflow-hidden"
      style={{
        borderColor: `${P.ACCENT}55`,
        background: `linear-gradient(180deg, ${P.BG}D9 0%, ${P.ACCENT}0A 100%)`,
      }}
      data-testid={`${testPrefix}-pricing-timeline`}
    >
      <div className="px-4 sm:px-5 py-3 border-b flex items-center gap-3" style={{ borderColor: "rgba(255,255,255,0.06)", background: `${P.ACCENT}0F` }}>
        <div
          className="w-8 h-8 rounded-sm border flex items-center justify-center shrink-0"
          style={{ borderColor: `${P.ACCENT}88`, background: `${P.ACCENT}18`, color: P.ACCENT }}
        >
          <Rocket size={14} />
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.36em] font-mono" style={{ color: P.ACCENT }}>1-2-12 Founder Terms</div>
          <div className="font-display text-white text-sm sm:text-base leading-tight">Three doors. One lifetime rate.</div>
        </div>
      </div>

      <div className="p-5 sm:p-6">
        <div className="hidden md:block relative">
          <div
            className="absolute left-6 right-6 top-[46px] h-0.5"
            style={{
              background: `linear-gradient(90deg, ${P.PRIMARY} 0%, ${P.ACCENT} 55%, ${P.ACCENT} 100%)`,
              opacity: 0.7,
            }}
          />
          <div className="grid grid-cols-3 gap-4 relative">
            {FOUNDER_TIMELINE.map((s, i) => (
              <TimelineNode key={s.key} P={P} stop={s} index={i} />
            ))}
          </div>
        </div>

        <div className="md:hidden space-y-4 relative">
          <div
            className="absolute top-3 bottom-3 left-6 w-0.5"
            style={{
              background: `linear-gradient(180deg, ${P.PRIMARY} 0%, ${P.ACCENT} 55%, ${P.ACCENT} 100%)`,
              opacity: 0.7,
            }}
          />
          {FOUNDER_TIMELINE.map((s) => (
            <MobileTimelineNode key={s.key} P={P} stop={s} />
          ))}
        </div>

        <div className="mt-6 rounded-sm border px-4 py-3 flex items-start gap-3" style={{ borderColor: `${P.PRIMARY}44`, background: `${P.PRIMARY}10` }}>
          <Sparkles size={14} style={{ color: P.PRIMARY }} className="mt-0.5" />
          <div className="text-[11px] leading-relaxed" style={{ color: P.INK }}>
            <span className="uppercase tracking-[0.22em] font-mono mr-2" style={{ color: P.PRIMARY }}>Founder rate ·</span>
            The $297/mo rate is locked from Month 12 forward. New Founder Nodes after Q1 2026 pay the full going rate.
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelineNode({ P, stop, index }) {
  // Stop tone from FOUNDER_TIMELINE is a hex, but overlay palette accent
  // so it doesn't clash. Fall back to palette PRIMARY / ACCENT.
  const tone = index === 0 ? P.PRIMARY : P.ACCENT;
  return (
    <div className="flex flex-col items-center text-center px-2">
      <div className="text-[9px] uppercase tracking-[0.32em] font-mono mb-2" style={{ color: tone }}>{stop.tag}</div>
      <div
        className="relative w-14 h-14 rounded-full border flex items-center justify-center mb-3"
        style={{
          borderColor: tone,
          background: `linear-gradient(160deg, ${tone}30 0%, ${P.BG}E6 65%)`,
          boxShadow: `0 0 22px -6px ${tone}`,
        }}
      >
        <span className="font-display text-lg" style={{ color: P.INK }}>{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="font-display text-2xl sm:text-3xl leading-none" style={{ color: tone }}>
        {stop.amount}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: P.INK }}>{stop.label}</div>
      <p className="mt-2 text-[12px] leading-relaxed" style={{ color: P.MUTED }}>{stop.body}</p>
    </div>
  );
}

function MobileTimelineNode({ P, stop }) {
  const tone = stop.key === "m1" ? P.PRIMARY : P.ACCENT;
  return (
    <div className="pl-14 relative">
      <div
        className="absolute left-3 top-1 w-6 h-6 rounded-full border flex items-center justify-center"
        style={{
          borderColor: tone,
          background: `${tone}30`,
          boxShadow: `0 0 12px -4px ${tone}`,
        }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: tone }} />
      </div>
      <div className="text-[10px] uppercase tracking-[0.32em] font-mono" style={{ color: tone }}>{stop.tag} · {stop.amount}</div>
      <div className="font-display text-white text-base leading-tight mt-0.5">{stop.label}</div>
      <p className="mt-1 text-[12px] leading-relaxed" style={{ color: P.MUTED }}>{stop.body}</p>
    </div>
  );
}

